/**
 * Components the AI APIs pages share (A7Y-5559): the error banner, the cost
 * basis and source badges and the data-age line.
 */
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@a7y/tailwind-catalyst';
import type { AiApiCostBasis, AiApiUsageSource } from '@a7y/types';
import { formatRelativeTime } from '@/lib/utils';
import { formatLocal } from '@/pages/audit-spend/format';
import { sourceLabel } from './meta';

/** The red banner `finance/PnlPage.tsx` uses, naming the endpoint that failed. */
export function ErrorBanner({ endpoint, error }: { endpoint: string; error: unknown }) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    <div
      role="alert"
      className="mb-6 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        Failed to load <code className="font-mono">{endpoint}</code>
        {message ? `: ${message}` : ''}
      </span>
    </div>
  );
}

const BASIS_LABEL: Record<AiApiCostBasis | 'none', string> = {
  billed: 'billed',
  rate_table: 'rate table',
  none: 'no data yet',
};

export function BasisBadge({ basis }: { basis: AiApiCostBasis | 'none' }) {
  return (
    <Badge color={basis === 'billed' ? 'green' : basis === 'rate_table' ? 'blue' : 'zinc'}>
      {BASIS_LABEL[basis]}
    </Badge>
  );
}

export function SourceBadge({ source }: { source: AiApiUsageSource }) {
  return (
    <Badge
      color={source === 'provider_cost' ? 'green' : source === 'provider_usage' ? 'blue' : 'amber'}
    >
      {sourceLabel(source)}
    </Badge>
  );
}

/** "Data from <relative time>", the full local time on hover: the age of the query, not of the response. */
export function DataAge({ generatedAt }: { generatedAt: string | undefined }) {
  if (!generatedAt) return null;
  return (
    <span className="text-xs text-zinc-500 dark:text-zinc-400" title={formatLocal(generatedAt)}>
      Data from {formatRelativeTime(generatedAt)}
    </span>
  );
}
